import React from 'react';
import { 
  ChildCare, 
  School, 
  MenuBook, 
  EmojiEvents, 
  MyLocation, 
  Handshake 
} from '@mui/icons-material';

const Academics = () => {
  return (
    <section id="academics" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-blue-900 mb-4">Academic Excellence</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">Discover our comprehensive curriculum designed to nurture intellectual growth and prepare students for future success</p>
        </div>

        <div className="flex flex-col gap-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-blue-800 mb-6 text-3xl font-semibold">Our Curriculum</h3>
              <p className="text-base md:text-lg leading-relaxed mb-6 text-gray-700">
                At Moxie Master Public School, we follow a comprehensive curriculum that combines 
                international standards with local relevance. Our approach focuses on developing critical 
                thinking, creativity, and practical skills that prepare students for the challenges of 
                tomorrow.
              </p>
              <p className="text-base md:text-lg leading-relaxed mb-0 text-gray-700">
                We emphasize project-based learning, collaborative activities, and real-world applications 
                to ensure that students not only acquire knowledge but also understand how to apply it 
                effectively in various contexts.
              </p>
            </div>
            <div className="flex justify-center">
              <div className="w-full h-72 bg-gradient-to-br from-blue-800 to-blue-900 rounded-xl flex items-center justify-center text-white text-xl font-medium shadow-lg">
                <span>Curriculum</span>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-blue-800 text-2xl font-semibold mb-8 text-center">Grade Levels</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-8 rounded-xl shadow-md border-l-4 border-blue-800 transition-transform hover:-translate-y-1 hover:shadow-lg">
                <div className="text-3xl text-blue-700 mb-4"><ChildCare fontSize="inherit" /></div>
                <h4 className="text-blue-900 font-semibold mb-2">Kindergarten</h4>
                <p className="text-gray-600 text-sm mb-4">A nurturing environment for our youngest learners to explore, play, and grow.</p>
                <ul className="list-none p-0 m-0 space-y-2">
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Play-based learning</li>
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Social-emotional development</li>
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Creative expression</li>
                </ul>
              </div>
              <div className="bg-gray-50 p-8 rounded-xl shadow-md border-l-4 border-blue-800 transition-transform hover:-translate-y-1 hover:shadow-lg">
                <div className="text-3xl text-blue-700 mb-4"><School fontSize="inherit" /></div>
                <h4 className="text-blue-900 font-semibold mb-2">Primary School</h4>
                <p className="text-gray-600 text-sm mb-4">Building strong foundations in literacy, numeracy, and critical thinking.</p>
                <ul className="list-none p-0 m-0 space-y-2">
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Core academic subjects</li>
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Project-based learning</li>
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Extracurricular activities</li>
                </ul>
              </div>
              <div className="bg-gray-50 p-8 rounded-xl shadow-md border-l-4 border-blue-800 transition-transform hover:-translate-y-1 hover:shadow-lg">
                <div className="text-3xl text-blue-700 mb-4"><MenuBook fontSize="inherit" /></div>
                <h4 className="text-blue-900 font-semibold mb-2">Middle & High School</h4>
                <p className="text-gray-600 text-sm mb-4">Preparing students for higher education and future careers through advanced academics.</p>
                <ul className="list-none p-0 m-0 space-y-2">
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">STEM & humanities</li>
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Leadership development</li>
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">College & career guidance</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h3 className="text-blue-800 text-2xl font-semibold mb-8 text-center">Teaching Approach</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-md text-center transition-transform hover:-translate-y-1 hover:shadow-lg">
                <div className="text-3xl text-blue-700 mb-4"><EmojiEvents fontSize="inherit" /></div>
                <h4 className="text-blue-900 font-semibold mb-2">Holistic Development</h4>
                <p className="text-gray-600 text-sm">Focusing on academic, social, emotional, and physical growth.</p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-md text-center transition-transform hover:-translate-y-1 hover:shadow-lg">
                <div className="text-3xl text-blue-700 mb-4"><MyLocation fontSize="inherit" /></div>
                <h4 className="text-blue-900 font-semibold mb-2">Experiential Learning</h4>
                <p className="text-gray-600 text-sm">Hands-on activities, field trips, and real-world projects.</p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-md text-center transition-transform hover:-translate-y-1 hover:shadow-lg">
                <div className="text-3xl text-blue-700 mb-4"><Handshake fontSize="inherit" /></div>
                <h4 className="text-blue-900 font-semibold mb-2">Collaborative Culture</h4>
                <p className="text-gray-600 text-sm">Encouraging teamwork, communication, and leadership.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Academics; 